import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Table, Space, Tooltip } from "antd";
import { useReactToPrint } from "react-to-print";
import StatusBadge from "./StatusBadge";
import SmartIcon from "../../components/generic/genericIcon/SmartIcon";
import CustomModal from "../../components/generic/custommodal/CustomModal";
import PrimaryButton from "../../components/generic/buttons/PrimaryButton";
import SecondaryButton from "../../components/generic/buttons/SecondaryButton";
import apiService from "../../service/apiservice/apiService";
import { useGetOrgIdentifier } from "../../experience/generic/hooks/useGetOrgIdentifier";
import { getCurrentTimeZone, getDateString } from "../../utils/dateutils";
import InvoiceViewContent from "../pages/InvoiceView";
import ReceiptViewContent from "../pages/ReceiptView";
import "../pages/billing.css";
import { useRecoilValue } from "recoil";
import { currentConfiguration } from "../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../experience/utils/configurationconstants";
import InvoiceDetails from "./InvoiceDetails";
import { billingSavedCardsState } from "../../components/recoil/billingSavedCardsStore";
import { publishEvent } from "../../components/generic/EventPublisher";
import MemoryType from "../../service/memory/enum/MemoryType";
import { useReadMemory } from "../../experience/generic/hooks/useReadMemory";

const formatCurrency = (value) =>
  typeof value === "number" ? `$${value.toFixed(2)}` : "—";

function billingPdfFilename(prefix, billId) {
  if (billId == null || billId === "") {
    return `${prefix.toLowerCase()}.pdf`;
  }
  const safe = String(billId).replace(/[^\w.-]+/g, "_");
  return `${prefix}_${safe}.pdf`;
}

function PdfModalBody({ contentRef, children }) {
  return (
    <div className="billing-pdf-modal-wrap">
      <div ref={contentRef} className="billing-pdf-root">
        {children}
      </div>
    </div>
  );
}

function sameInvoiceRecord(a, b) {
  if (!a || !b) return false;
  if (a.id != null && b.id != null) return a.id === b.id;
  const aKey = a.invoice?.invoiceId;
  const bKey = b.invoice?.invoiceId;
  if (aKey != null && bKey != null) return aKey === bKey;
  return false;
}

function pickLatestInvoice(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return [...rows].sort((a, b) => {
    const ta = a.billingDate ? new Date(a.billingDate).getTime() : -Infinity;
    const tb = b.billingDate ? new Date(b.billingDate).getTime() : -Infinity;
    if (tb !== ta) return tb - ta;
    const idA = Number(a.id);
    const idB = Number(b.id);
    if (!Number.isNaN(idA) && !Number.isNaN(idB) && idA !== idB) {
      return idB - idA;
    }
    return String(b.invoiceNumber ?? "").localeCompare(
      String(a.invoiceNumber ?? ""),
    );
  })[0];
}

function modalFooter({
  onClose,
  onPrint,
  showApprove = false,
  onApprove,
  approveLoading = false,
  /** No saved payment methods — approve (pay) is not possible. */
  approveDisabledNoCards = false,
  /** Shown on Approve when disabled because there are no saved cards (after load). */
  approveNoCardsTooltip = null,
  showPrint = true,
}) {
  const approveDisabled = approveLoading || approveDisabledNoCards;
  const closeDisabled = approveLoading;
  const approveButton = (
    <PrimaryButton
      buttonText="Approve"
      loading={approveLoading}
      buttonDisable={approveDisabled}
      handleOnClick={onApprove}
    />
  );
  return (
    <Space
      size={12}
      wrap
      align="center"
      className="billing-modal-footer-actions"
    >
      <SecondaryButton
        buttonText="Close"
        handleOnClick={onClose}
        buttonDisable={closeDisabled}
      />
      {showPrint && typeof onPrint === "function" ? (
        <PrimaryButton
          buttonText="Print / Save as PDF"
          buttonDisable={approveLoading}
          handleOnClick={onPrint}
        />
      ) : null}
      {showApprove && typeof onApprove === "function" ? (
        approveNoCardsTooltip ? (
          <Tooltip title={approveNoCardsTooltip}>
            <span className="billing-approve-disabled-tooltip-wrap">
              {approveButton}
            </span>
          </Tooltip>
        ) : (
          approveButton
        )
      ) : null}
    </Space>
  );
}

const InvoiceTable = ({ onPaymentSuccess, officeId }) => {
  const organizationIdentifier = useGetOrgIdentifier();
  const userJson = useReadMemory(MemoryType.USER);
  const invoiceRef = useRef(null);
  const receiptRef = useRef(null);
  const configuration = useRecoilValue(currentConfiguration);
  const { list: savedCards, loading: savedCardsLoading } = useRecoilValue(
    billingSavedCardsState,
  );
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [record, setRecord] = useState(null);
  const [allowApproveInvoice, setAllowApproveInvoice] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);

  const openInvoiceModal = useCallback((rec) => {
    setRecord(rec);
    setInvoiceModalOpen(true);
  }, []);

  const openReceiptModal = useCallback((rec) => {
    setRecord(rec);
    setReceiptModalOpen(true);
  }, []);

  const openDetailsModal = useCallback((rec) => {
    setRecord(rec);
    setDetailsModalOpen(true);
  }, []);

  const closeInvoiceModal = useCallback(() => {
    if (approveLoading) return;
    setInvoiceModalOpen(false);
    setRecord(null);
  }, [approveLoading]);

  const closeReceiptModal = useCallback(() => {
    setReceiptModalOpen(false);
    setRecord(null);
  }, []);

  const closeDetailsModal = useCallback(() => {
    setDetailsModalOpen(false);
    setRecord(null);
  }, []);

  const printInvoice = useReactToPrint({
    contentRef: invoiceRef,
    documentTitle: () =>
      billingPdfFilename("Invoice", record?.invoice?.invoiceId),
  });

  const printReceipt = useReactToPrint({
    contentRef: receiptRef,
    documentTitle: () =>
      billingPdfFilename("Receipt", record?.receipt?.invoiceId),
  });

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.get(
        "niramayafoundation/billing/api/v1/invoices",
        {
          organizationIdentifier,
          timezone: getCurrentTimeZone(),
          ...(officeId != null && { officeId }),
        },
      );
      setInvoices(Array.isArray(response) ? response : []);
    } finally {
      setLoading(false);
    }
  }, [officeId, organizationIdentifier]);

  const handleApproveInvoice = useCallback(async () => {
    if (savedCardsLoading || savedCards.length === 0) return;
    const invoiceId = record?.id;
    setApproveLoading(true);
    try {
      const result = await apiService.post(
        "niramayafoundation/billing/api/v1/pay",
        {
          organizationIdentifier,
          invoiceId,
          ...(officeId != null && { officeId }),
        },
        "Invoice approved successfully",
        "Could not approve invoice",
      );
      if (result !== undefined) {
        await fetchInvoices();
        onPaymentSuccess?.();
        closeInvoiceModal();
        publishEvent("niramaya.billing.invoice.approved", userJson, result);
      }
    } finally {
      setApproveLoading(false);
    }
  }, [
    closeInvoiceModal,
    fetchInvoices,
    onPaymentSuccess,
    officeId,
    organizationIdentifier,
    record?.id,
    savedCards.length,
    savedCardsLoading,
    userJson,
  ]);

  const columns = useMemo(
    () => [
      {
        title: "Invoice Number",
        dataIndex: "invoiceNumber",
        key: "invoiceNumber",
        ellipsis: true,
        render: (_, record) =>
          record?.invoice?.invoiceId ?? record?.invoiceNumber ?? "—",
      },
      {
        title: "Billing Date",
        dataIndex: "billingDate",
        key: "billingDate",
        render: (_, record) =>
          record?.billingDate != null && record.billingDate !== ""
            ? getDateString(record.billingDate)
            : "—",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (_, record) => {
          const settledIn = record?.settledInInvoiceId;
          return (
            <div className="billing-invoice-status-cell">
              <StatusBadge status={record?.status} />
              {settledIn != null && settledIn !== "" ? (
                <span className="billing-invoice-status-settled">
                  Settled In Invoice:{" "}
                  <span className="billing-invoice-status-settled-id">
                    {settledIn}
                  </span>
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        title: "Charges",
        dataIndex: "charges",
        key: "charges",
        render: (val) => formatCurrency(val),
      },
      {
        title: "Payments",
        dataIndex: "payment",
        key: "payment",
        render: (val, record) => formatCurrency(val ?? record?.payments),
      },
      {
        title: "Balance",
        dataIndex: "accountBalance",
        key: "accountBalance",
        render: (val) => formatCurrency(val),
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <Space size="middle" className="billing-table-actions">
            <Tooltip title="View Invoice" placement="bottom">
              <>
                <SmartIcon
                  iconKey="invoice"
                  handleIconClick={() => openInvoiceModal(record)}
                />
              </>
            </Tooltip>
            {record.receipt && (
              <Tooltip title="View Receipt" placement="bottom">
                <>
                  <SmartIcon
                    iconKey="receipt"
                    handleIconClick={() => openReceiptModal(record)}
                  />
                </>
              </Tooltip>
            )}
            <Tooltip title="View Form Summary" placement="bottom">
              <>
                <SmartIcon
                  iconKey="forms-summary"
                  handleIconClick={() => openDetailsModal(record)}
                />
              </>
            </Tooltip>
          </Space>
        ),
      },
    ],
    [openDetailsModal, openInvoiceModal, openReceiptModal],
  );

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    // access control for approve button
    const configurationAllowApproveInvoice =
      configuration[ConfigurationConstants.FEATUREACCESS_INVOICE_APPROVE];
    setAllowApproveInvoice(configurationAllowApproveInvoice?.visibility);
  }, [configuration]);

  const latestInvoice = useMemo(() => pickLatestInvoice(invoices), [invoices]);

  return (
    <>
      <h5 className="font-weight-bold mb-2 mt-4">Invoices</h5>
      <Table
        className="tableStyling"
        rowKey={(r, index) =>
          r?.id ??
          r?.invoiceNumber ??
          r?.invoice?.invoiceId ??
          `invoice-row-${index}`
        }
        columns={columns}
        dataSource={invoices}
        loading={loading}
        pagination={false}
      />

      {invoiceModalOpen && (
        <CustomModal
          isModalOpen={invoiceModalOpen}
          closeModal={closeInvoiceModal}
          footer={modalFooter({
            onClose: closeInvoiceModal,
            onPrint: printInvoice,
            showApprove:
              allowApproveInvoice &&
              !record?.receipt &&
              sameInvoiceRecord(record, latestInvoice),
            onApprove: handleApproveInvoice,
            approveLoading,
            approveDisabledNoCards:
              savedCardsLoading || savedCards.length === 0,
            approveNoCardsTooltip:
              !savedCardsLoading && savedCards.length === 0
                ? "Payment method required to approve"
                : null,
          })}
          closable={false}
          cssClassName="billing-modal-footer-wrap"
        >
          <PdfModalBody contentRef={invoiceRef}>
            <InvoiceViewContent invoice={record?.invoice} />
          </PdfModalBody>
        </CustomModal>
      )}

      {receiptModalOpen && (
        <CustomModal
          isModalOpen={receiptModalOpen}
          closeModal={closeReceiptModal}
          footer={modalFooter({
            onClose: closeReceiptModal,
            onPrint: printReceipt,
          })}
          closable={false}
          cssClassName="modelStyleClassName billing-modal-footer-wrap"
        >
          <PdfModalBody contentRef={receiptRef}>
            <ReceiptViewContent receipt={record?.receipt} />
          </PdfModalBody>
        </CustomModal>
      )}

      {detailsModalOpen && (
        <CustomModal
          className="invoice-details-modal"
          isModalOpen={detailsModalOpen}
          closeModal={closeDetailsModal}
          title="Invoice summary"
          footer={modalFooter({
            onClose: closeDetailsModal,
            showPrint: false,
          })}
          closable={false}
          cssClassName="modelStyleClassName billing-modal-footer-wrap"
        >
          <InvoiceDetails
            billingPeriodStart={record?.invoice?.billingPeriodStart}
            billingPeriodEnd={record?.invoice?.billingPeriodEnd}
            organizationTimeZone={record?.invoice?.organizationTimeZone}
            officeId={officeId}
          />
        </CustomModal>
      )}
    </>
  );
};

export default InvoiceTable;
